/**
 * profile-api.ts - API client for user profile operations
 *
 * Handles all HTTP requests related to user profile management.
 */

export interface UserProfile {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  rating?: number;
  ratingStd?: number;    // FIDE Standard/Classical rating
  ratingRapid?: number;  // FIDE Rapid rating
  ratingBlitz?: number;  // FIDE Blitz rating
  title?: string;
  birthDate?: string;
  gender?: 'M' | 'F';
  club?: string;
  fideId?: string;
  federationCode?: string;
  federationId?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ProfileCompleteness {
  isComplete: boolean;
  completionPercentage: number;
  missingFields: string[];
}

/**
 * Fetch user's profile
 * @returns User profile object or null if not found
 * @throws Error if request fails
 */
export async function fetchProfile(): Promise<UserProfile | null> {
  const response = await fetch('/api/profile', {
    credentials: 'include'
  });

  if (response.status === 404) {
    return null;
  }

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to fetch profile');
  }

  const data = await response.json();
  return data.profile;
}

/**
 * Create or update user profile
 * @param profileData - Profile data
 * @returns Updated profile object
 * @throws Error if request fails
 */
export async function upsertProfile(profileData: Partial<UserProfile>): Promise<UserProfile> {
  const response = await fetch('/api/profile', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(profileData)
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to save profile');
  }

  const data = await response.json();
  return data.profile;
}

/**
 * Delete user profile
 * @throws Error if request fails
 */
export async function deleteProfile(): Promise<void> {
  const response = await fetch('/api/profile', {
    method: 'DELETE',
    credentials: 'include'
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to delete profile');
  }
}

/**
 * Check profile completeness
 * @returns Profile completeness information
 * @throws Error if request fails
 */
export async function checkProfileCompleteness(): Promise<ProfileCompleteness> {
  const response = await fetch('/api/profile/completeness', {
    credentials: 'include'
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to check profile completeness');
  }

  const data = await response.json();
  return data.completeness;
}
